import styled from 'styled-components'
import Link from 'next/link'
import PropTypes from 'prop-types'
import Wrapper from 'common/wrapper'
import { Typography } from 'common/typography'
import Price from 'components/pricing/server-price'
import { SecondaryButton } from 'common/button'
import { useSubscriptionPlans } from 'querys/subscriptions'
import useSession from 'querys/session'
import { useCountryCode } from 'querys/payment'
import DiscountTimer from 'common/banner/discount-timer'

const PricingBannerStyled = styled.div`
  background: var(--sky-inverted);
  color: var(--basePrimary);
  position: ${({ fixed }) => (fixed ? 'fixed' : 'relative')};
  ${({ fixed }) => (fixed && `
    right: 0;
    left: 0;
    bottom: 0;
  `)};
  padding: 16px 0;
  z-index: 2;
  opacity: 1;
  transition: opacity 0.2s ease-in-out;
  a {
    height: 32px;
  }
  &.opacity {
    opacity: 0;
  }
  .banner-buttons {
    margin-top: 8px;
  }
  .banner-details {
    display: flex;
    justify-content: center;
    flex-direction: column;
  }
  .free {
    border: 1px solid var(--secondary);
    background: var(--secondary);
    color: white;
    border-radius: .5em;
    padding: .2em;
    font-size: 14px;
    display: inline-block;
  }
  .discount {
    color: var(--neutral90)!important;
  }
  .free-course {
    font-size: 16px;
    line-height: 24px;
    color: var(--secondary);
    font-weight: bold;
  }
  .princing-banner-container {
    display: grid;
    grid-template-columns: 1fr minmax(170px, 1fr);
    grid-template-rows: repeat(2, min-content);
    column-gap: 0px;
    align-items: center;
    justify-items: flex-end;
  }
  .banner-buttons {
    grid-area: 2 / 1 / 3 / 3;
    width: 100%;
  }
  h3 {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }
  @media(min-width: 768px) {
    h3 {
      display: block;
    }
    .free-course {
      font-size: 24px;
      line-height: 32px;
      color: var(--secondary);
      font-weight: bold;
    }
    a {
      height: 48px;
    }
    .princing-banner-container {
      display: grid;
      grid-template-columns: repeat(2, 1fr) min-content;
      align-items: center;
      justify-items: flex-end;
    }
    .banner-details {
      justify-self: flex-start;
    }
    .banner-buttons {
      grid-area: initial;
      width: auto;
    }
  }
  @media(min-width: 1024px) {
    position: ${({ fixed }) => (fixed ? 'fixed' : 'static')};
    left: 0;
    right: 0;
    bottom: 0;
  }
`

function PricingBanner({
  hidden, isFree, fixed, slug, opacity,
}) {
  // Both projects and courses have title
  const { data: { countryCode } } = useCountryCode()
  const currencyCode = countryCode === 'MX' ? 'MXN' : 'USD'
  const amount = countryCode === 'MX' ? (8400 / 12) : (420 / 12)
  const { data: totalSubscriptionPlans, isError } = useSubscriptionPlans()
  const { token } = useSession()

  if (isError) return null

  const {
    year: {
      price, discount,
    },
  } = totalSubscriptionPlans
  const totalAmount = price.amount
  const monthDiscount = {
    currency_code: currencyCode, amount,
  }

  // If has access to a project or if has an enrolment to a project, return null
  if (hidden) return null

  if (isFree) {
    return (
      <PricingBannerStyled role="contentinfo" aria-label="Banner de invitacion a un curso gratuito" fixed={fixed}>
        <Wrapper maxWidth="1200">
          <div className="princing-banner-container" data-cy="free-banner">
            <div className="banner-details">
              <p className="free-course">Curso Gratuito</p>
            </div>
            <div className="banner-buttons">
              {!token ? (
                <Link href={`/login?next=/cursos/${slug}`} passHref>
                  <SecondaryButton fullWidth fitContent>
                    Inscríbete
                  </SecondaryButton>
                </Link>
              ) : (
                <Link href={`/cursos/${slug}`} passHref>
                  <SecondaryButton fullWidth fitContent>
                    Unirme al curso
                  </SecondaryButton>
                </Link>
              )}
            </div>
          </div>
        </Wrapper>
      </PricingBannerStyled>
    )
  }

  return (
    <Link href="/precios" passHref>
      <PricingBannerStyled className={opacity && 'opacity'} id="pricing-banner" role="region" aria-label="Banner de precios" fixed={fixed}>
        <Wrapper maxWidth="1200">
          <div className="princing-banner-container" data-cy="pricing-banner">
            <div className="banner-details">
              <Typography as="h3" small="smallBold" large="p2Bold">
                Suscríbete a todos los cursos, proyectos y talleres
              </Typography>
              <Price
                price={{ ...price, amount: Math.round(totalAmount / 12) }}
                directDiscount={monthDiscount}
                originalPrice={price}
                limitedTime={false}
                small
              />
            </div>
            <DiscountTimer alingSmall="end" aling="start" discount={discount} />
            <div className="banner-buttons">
              <SecondaryButton fullWidth fitContent id="pricing-plans">
                Ver planes
              </SecondaryButton>
            </div>
          </div>
        </Wrapper>
      </PricingBannerStyled>
    </Link>
  )
}

PricingBanner.defaultProps = {
  fixed: false,
}

PricingBanner.propTypes = {
  fixed: PropTypes.bool,
}

export default PricingBanner
